export const unavailableSize = (size) =>
  size?.stock?.is_sold_out || size?.stock?.quantity <= 0

export const isAvailableSize = (size) =>
  !!size?.id_product_attribute &&
  !!size.stock &&
  !unavailableSize(size) &&
  (size.stock.is_sold_out === false || size.stock.quantity > 0)

// A successful HTTP response alone is insufficient: confirm the selected SKU.
export const getCartQuantity = (cart, productId, attributeId) => {
  if (cart?.error || cart?.ok === false || !Array.isArray(cart?.items))
    return null
  return cart.items.reduce((quantity, item) => {
    if (quantity == null || item?.id_product == null) return null
    if (String(item.id_product) === String(productId)) {
      if (!Array.isArray(item.attributes)) return null
      if (
        item.attributes.some(
          (attribute) =>
            attribute?.selected &&
            String(attribute.id_product_attribute) === String(attributeId),
        )
      ) {
        const count = Number(item.quantity)
        if (
          item.quantity == null ||
          item.quantity === '' ||
          !Number.isInteger(count) ||
          count < 0
        )
          return null
        return quantity + count
      }
    }
    return quantity
  }, 0)
}

const isStockError = (response) =>
  ['ITEM_SOLD_OUT', 'QUANTITY_OVER_LIMIT'].includes(
    response?.error?.code || response?.error?.error?.code,
  )

export default function createQuickAddRequest({
  productId,
  getProduct,
  addToBag,
  refData,
  onState,
  onProduct = () => {},
}) {
  let active
  let disposed = false
  let uncertain = false

  const add = async (size) => {
    if (disposed || active || uncertain || !size?.id_product_attribute) return
    const request = {}
    active = request
    const complete = (status) => {
      if (active !== request) return
      uncertain = status === 'uncertain'
      if (!disposed) onState({ status, size })
      active = null
    }
    let phase = 'prepare'
    let outcome = 'retry'
    let beforeQuantity
    let confirmed = false
    onState({ status: 'adding', size })
    try {
      const product = await getProduct(productId)
      if (disposed) return
      if (
        product?.error ||
        product?.ok === false ||
        String(product?.id_product) !== String(productId) ||
        !Array.isArray(product?.attributes)
      )
        throw new Error('Product could not be verified')
      onProduct(product)
      const freshSize = product.attributes.find(
        (attribute) =>
          String(attribute?.id_product_attribute) ===
          String(size.id_product_attribute),
      )
      if (
        product.is_coming_soon ||
        !isAvailableSize(freshSize) ||
        unavailableSize(product)
      ) {
        complete('unavailable')
        return
      }

      await addToBag({
        enableInterstitial: false,
        product,
        sizeData: { ...size, ...freshSize },
        refData,
        validateCartResult: (response, stage) => {
          if (stage === 'prepare') {
            if (disposed) return false
            beforeQuantity = getCartQuantity(
              response,
              productId,
              size.id_product_attribute,
            )
            if (beforeQuantity == null) return false
            phase = 'add'
            outcome = 'uncertain'
            return true
          }
          const afterQuantity = getCartQuantity(
            response,
            productId,
            size.id_product_attribute,
          )
          if (afterQuantity == null) {
            outcome = isStockError(response) ? 'unavailable' : 'uncertain'
            return false
          }
          confirmed = afterQuantity > beforeQuantity
          return confirmed
        },
        onError: () => {},
        // Stop the spinner as soon as the cart is confirmed, before analytics finish.
        onSuccess: () => {
          if (confirmed) complete('success')
        },
      })
      complete(confirmed ? 'success' : outcome)
    } catch (error) {
      const failure = phase === 'add' ? 'uncertain' : 'retry'
      complete(confirmed ? 'success' : failure)
    } finally {
      if (active === request) active = null
    }
  }

  return {
    add,
    dispose: () => {
      disposed = true
    },
  }
}
